"use client";

import { useMemo, useState } from "react";
import {
  AlertTriangle,
  Calendar,
  ChevronRight,
  FolderKanban,
  GitBranch,
  LayoutList,
  Radio,
} from "lucide-react";
import { useStore } from "@/lib/store";
import { PROJECT_STATUSES } from "@/lib/constants";
import { deriveProjectInsights, type ProjectHealthKind } from "@/lib/projectInsights";
import { useProjectCommandData } from "@/lib/useProjectCommandData";
import type { Project, ProjectMilestone } from "@/lib/types";
import { cn, formatDate, formatRelative } from "@/lib/utils";

type PortfolioMode = "overview" | "roadmap";

export function ProjectsView({ onOpenProject }: { onOpenProject: (projectId: string) => void }) {
  const activeWorkspaceId = useStore((s) => s.activeWorkspaceId);
  const allProjects = useStore((s) => s.projects);
  const issues = useStore((s) => s.issues);
  const progressCards = useStore((s) => s.progressCards);
  const docs = useStore((s) => s.docs);
  const agentRuns = useStore((s) => s.agentRuns);
  const [mode, setMode] = useState<PortfolioMode>("overview");
  const { sources, milestones: commandMilestones } = useProjectCommandData({
    workspaceId: activeWorkspaceId,
  });

  const projects = useMemo(
    () =>
      allProjects
        .filter((project) => project.workspaceId === activeWorkspaceId)
        .sort(compareProjects),
    [activeWorkspaceId, allProjects]
  );

  const rows = useMemo(
    () =>
      projects.map((project) => ({
        project,
        insight: deriveProjectInsights({ project, issues, progressCards, docs, agentRuns }),
        sources: sources.filter((source) => source.projectId === project.id),
        milestones: commandMilestones.filter((milestone) => milestone.projectId === project.id),
      })),
    [agentRuns, commandMilestones, docs, issues, progressCards, projects, sources]
  );

  const portfolio = useMemo(
    () => ({
      active: rows.filter((row) => row.project.status === "in_progress").length,
      attention: rows.filter((row) => row.insight.health.kind === "needs_attention").length,
      review: rows.reduce((sum, row) => sum + row.insight.counts.review, 0),
      shipped: rows.reduce((sum, row) => sum + row.insight.counts.doneLast7d, 0),
    }),
    [rows]
  );

  return (
    <div className="flex-1 overflow-y-auto scrollbar-thin">
      <div className="mx-auto w-full max-w-[1280px] px-4 py-5 md:px-6">
        <div className="mb-5 flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <div className="mb-1 flex items-center gap-2 text-[11px] font-medium uppercase tracking-wider text-[var(--fg-muted)]">
              <Radio size={12} className="text-[var(--green)]" />
              Live portfolio
            </div>
            <h2 className="text-[24px] font-medium tracking-tight text-[var(--fg)]">
              Project command center
            </h2>
            <p className="mt-1 max-w-2xl text-[12.5px] text-[var(--fg-tertiary)]">
              One view of what is moving, what needs attention, and what ships next.
            </p>
          </div>

          <div className="flex items-center gap-1 rounded-md border border-[var(--border)] bg-[var(--bg-secondary)] p-1">
            <ModeButton active={mode === "overview"} onClick={() => setMode("overview")}>
              <LayoutList size={12} /> Overview
            </ModeButton>
            <ModeButton active={mode === "roadmap"} onClick={() => setMode("roadmap")}>
              <Calendar size={12} /> Roadmap
            </ModeButton>
          </div>
        </div>

        <div className="mb-4 grid grid-cols-2 gap-px overflow-hidden rounded-md border border-[var(--border)] bg-[var(--border)] md:grid-cols-4">
          <PortfolioMetric label="Active fronts" value={portfolio.active} tone="default" />
          <PortfolioMetric label="Need attention" value={portfolio.attention} tone={portfolio.attention ? "danger" : "default"} />
          <PortfolioMetric label="In review" value={portfolio.review} tone="default" />
          <PortfolioMetric label="Done · 7 days" value={portfolio.shipped} tone="success" />
        </div>

        {rows.length === 0 ? (
          <EmptyPortfolio />
        ) : mode === "overview" ? (
          <div className="overflow-hidden rounded-md border border-[var(--border)] bg-[var(--bg-secondary)]">
            <div className="hidden grid-cols-[minmax(260px,1.7fr)_minmax(220px,1.2fr)_minmax(230px,1fr)_116px_24px] gap-4 border-b border-[var(--border)] px-4 py-2.5 text-[11px] font-medium uppercase tracking-wider text-[var(--fg-muted)] lg:grid">
              <span>Project</span>
              <span>Tasks</span>
              <span>Pulse</span>
              <span>Target</span>
              <span />
            </div>
            {rows.map((row) => (
              <PortfolioRow key={row.project.id} {...row} onOpen={() => onOpenProject(row.project.id)} />
            ))}
          </div>
        ) : (
          <Roadmap rows={rows} onOpenProject={onOpenProject} />
        )}
      </div>
    </div>
  );
}

function PortfolioRow({
  project,
  insight,
  sources,
  milestones,
  onOpen,
}: {
  project: Project;
  insight: ReturnType<typeof deriveProjectInsights>;
  sources: { id: string; repository: string }[];
  milestones: ProjectMilestone[];
  onOpen: () => void;
}) {
  const status = PROJECT_STATUSES.find((item) => item.id === project.status);
  const now = insight.latestProgressCard?.summary.trim() || fallbackNow(insight);
  const totalSegments = Math.max(insight.counts.committed, 1);
  const segments = [
    { value: insight.counts.todo, color: "var(--fg-muted)", label: "Next" },
    { value: insight.counts.doing, color: "var(--yellow)", label: "Doing" },
    { value: insight.counts.review, color: "var(--purple)", label: "Review" },
    { value: insight.counts.done, color: "var(--green)", label: "Done" },
  ];

  return (
    <button
      type="button"
      onClick={onOpen}
      className="group grid w-full grid-cols-1 gap-4 border-b border-[var(--border)] px-4 py-4 text-left last:border-b-0 hover:bg-[var(--bg-hover)] focus-ring transition-colors lg:grid-cols-[minmax(260px,1.7fr)_minmax(220px,1.2fr)_minmax(230px,1fr)_116px_24px] lg:items-center"
    >
      <div className="flex min-w-0 gap-3">
        <div
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md border text-[17px]"
          style={{ color: project.color, borderColor: `${project.color}66`, background: `${project.color}18` }}
        >
          {project.icon}
        </div>
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="truncate text-[13.5px] font-medium text-[var(--fg)]">{project.name}</span>
            <HealthChip kind={insight.health.kind} label={insight.health.label} />
          </div>
          <p className="mt-1 line-clamp-1 text-[11.5px] text-[var(--fg-tertiary)]">{now}</p>
          <div className="mt-2 flex flex-wrap items-center gap-2 text-[10.5px] text-[var(--fg-muted)]">
            <span className="inline-flex items-center gap-1">
              <span className="h-1.5 w-1.5 rounded-full" style={{ background: status?.color }} />
              {status?.name}
            </span>
            {sources.length > 0 && (
              <span className="inline-flex items-center gap-1">
                <GitBranch size={10} />
                {sources.length === 1 ? sources[0].repository : `${sources.length} repositories`}
              </span>
            )}
            {milestones.length > 0 && (
              <span className="inline-flex items-center gap-1">
                <Calendar size={10} />
                {milestones.length} dated goal{milestones.length === 1 ? "" : "s"}
              </span>
            )}
          </div>
        </div>
      </div>

      <div>
        <div className="mb-2 flex h-1.5 overflow-hidden rounded-full bg-[var(--bg-tertiary)]">
          {segments.map((segment) =>
            segment.value ? (
              <span
                key={segment.label}
                title={`${segment.label}: ${segment.value}`}
                style={{ width: `${(segment.value / totalSegments) * 100}%`, background: segment.color }}
              />
            ) : null
          )}
        </div>
        <div className="grid grid-cols-4 gap-2">
          <MiniMetric label="Doing" value={insight.counts.doing} />
          <MiniMetric label="Review" value={insight.counts.review} />
          <MiniMetric label="Done 7d" value={insight.counts.doneLast7d} />
          <MiniMetric label="Attention" value={insight.attentionCount} danger={insight.attentionCount > 0} />
        </div>
      </div>

      <div className="min-w-0">
        <p className="line-clamp-2 text-[11.5px] text-[var(--fg-secondary)]">{insight.health.reason}</p>
        <p className="mt-1 text-[10.5px] text-[var(--fg-muted)]">
          Last signal {formatRelative(insight.latestSignalAt)}
        </p>
      </div>

      <div className="flex items-center gap-1.5 text-[11px] text-[var(--fg-tertiary)] lg:block">
        <Calendar size={11} className="lg:mb-1" />
        <span>{project.targetDate ? formatDate(project.targetDate) : "Needs date"}</span>
      </div>

      <ChevronRight size={14} className="hidden text-[var(--fg-muted)] group-hover:text-[var(--fg-secondary)] lg:block" />
    </button>
  );
}

function Roadmap({
  rows,
  onOpenProject,
}: {
  rows: Array<{
    project: Project;
    insight: ReturnType<typeof deriveProjectInsights>;
    milestones: ProjectMilestone[];
  }>;
  onOpenProject: (projectId: string) => void;
}) {
  const today = startOfDay(Date.now());
  const dated = rows.filter(
    (row) =>
      (row.project.startDate && row.project.targetDate) || row.milestones.length > 0
  );
  const undated = rows.filter(
    (row) =>
      (!row.project.startDate || !row.project.targetDate) && row.milestones.length === 0
  );
  const timestamps = dated.flatMap((row) => [
    ...(row.project.startDate ? [row.project.startDate] : []),
    ...(row.project.targetDate ? [row.project.targetDate] : []),
    ...row.milestones.map((milestone) => milestone.targetDate),
  ]);
  const min = timestamps.length ? Math.min(today, ...timestamps) - 7 * DAY : today - 14 * DAY;
  const max = timestamps.length ? Math.max(today, ...timestamps) + 7 * DAY : today + 30 * DAY;
  const span = Math.max(max - min, DAY);
  const todayLeft = ((today - min) / span) * 100;

  return (
    <div className="overflow-hidden rounded-md border border-[var(--border)] bg-[var(--bg-secondary)]">
      <div className="grid grid-cols-[220px_minmax(480px,1fr)] border-b border-[var(--border)] text-[10.5px] text-[var(--fg-muted)]">
        <div className="px-4 py-3 font-medium uppercase tracking-wider">Project</div>
        <div className="relative px-4 py-3">
          <span>{formatDate(min)}</span>
          <span className="absolute left-1/2 -translate-x-1/2">Today</span>
          <span className="float-right">{formatDate(max)}</span>
        </div>
      </div>
      <div className="overflow-x-auto scrollbar-thin">
        <div className="min-w-[760px]">
          {dated.map(({ project, insight, milestones }) => {
            const left = project.startDate ? ((project.startDate - min) / span) * 100 : null;
            const width =
              project.startDate && project.targetDate
                ? Math.max(((project.targetDate - project.startDate) / span) * 100, 1.5)
                : null;
            const attentionLeft = project.targetDate
              ? ((project.targetDate - min) / span) * 100
              : milestones.length
                ? ((milestones[milestones.length - 1].targetDate - min) / span) * 100
                : 100;
            return (
              <button
                key={project.id}
                type="button"
                onClick={() => onOpenProject(project.id)}
                className="group grid w-full grid-cols-[220px_minmax(480px,1fr)] border-b border-[var(--border)] text-left last:border-b-0 hover:bg-[var(--bg-hover)] transition-colors"
              >
                <div className="flex items-center gap-2 px-4 py-4">
                  <span>{project.icon}</span>
                  <span className="truncate text-[12px] font-medium">{project.name}</span>
                </div>
                <div className="relative mx-4 my-5 h-7 rounded bg-[var(--bg-tertiary)]">
                  <span className="absolute inset-y-[-16px] w-px bg-[var(--border-strong)]" style={{ left: `${todayLeft}%` }} />
                  {left !== null && width !== null && (
                    <span
                      className="absolute inset-y-1.5 rounded-sm opacity-60"
                      style={{ left: `${left}%`, width: `${width}%`, background: project.color }}
                      title={`${formatDate(project.startDate!)} → ${formatDate(project.targetDate!)}`}
                    />
                  )}
                  {milestones.map((milestone, index) => {
                    const markerLeft = ((milestone.targetDate - min) / span) * 100;
                    const markerTone =
                      milestone.status === "achieved"
                        ? "var(--green)"
                        : milestone.status === "at_risk"
                          ? "var(--red)"
                          : milestone.status === "in_progress"
                            ? "var(--blue)"
                            : "var(--fg-muted)";
                    return (
                      <span
                        key={milestone.id}
                        className="absolute top-1/2 h-3 w-3 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-[var(--bg-secondary)]"
                        style={{ left: `${markerLeft}%`, background: markerTone }}
                        title={`Goal ${String.fromCharCode(65 + index)} · ${milestone.title} · ${formatDate(milestone.targetDate)}`}
                      >
                        <span className="absolute bottom-[12px] left-1/2 -translate-x-1/2 text-[8px] font-medium text-[var(--fg-tertiary)]">
                          {index < 26 ? String.fromCharCode(65 + index) : index + 1}
                        </span>
                      </span>
                    );
                  })}
                  {insight.attentionCount > 0 && (
                    <AlertTriangle
                      size={11}
                      className="absolute top-1/2 -translate-y-1/2 text-[var(--red)]"
                      style={{ left: `min(calc(${attentionLeft}% + 4px), calc(100% - 14px))` }}
                    />
                  )}
                </div>
              </button>
            );
          })}
        </div>
      </div>
      {undated.length > 0 && (
        <div className="border-t border-[var(--border)] px-4 py-3">
          <div className="mb-2 text-[11px] font-medium uppercase tracking-wider text-[var(--fg-muted)]">Needs dates</div>
          <div className="flex flex-wrap gap-2">
            {undated.map(({ project }) => (
              <button
                key={project.id}
                type="button"
                onClick={() => onOpenProject(project.id)}
                className="inline-flex items-center gap-2 rounded-md border border-[var(--border)] bg-[var(--bg-tertiary)] px-2.5 py-1.5 text-[11.5px] hover:bg-[var(--bg-hover)] transition-colors"
              >
                <span>{project.icon}</span>
                {project.name}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function ModeButton({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "inline-flex items-center gap-1.5 rounded px-2.5 py-1.5 text-[11.5px] font-medium transition-colors",
        active ? "bg-[var(--bg-active)] text-[var(--fg)]" : "text-[var(--fg-tertiary)] hover:bg-[var(--bg-hover)]"
      )}
    >
      {children}
    </button>
  );
}

function PortfolioMetric({ label, value, tone }: { label: string; value: number; tone: "default" | "danger" | "success" }) {
  return (
    <div className="bg-[var(--bg-secondary)] px-4 py-3">
      <div className="text-[10.5px] font-medium uppercase tracking-wider text-[var(--fg-muted)]">{label}</div>
      <div className={cn("mt-1 text-[20px] font-medium tracking-tight", tone === "danger" ? "text-[var(--red)]" : tone === "success" ? "text-[var(--green)]" : "text-[var(--fg)]")}>{value}</div>
    </div>
  );
}

function MiniMetric({ label, value, danger = false }: { label: string; value: number; danger?: boolean }) {
  return (
    <div>
      <div className={cn("text-[12px] font-medium", danger ? "text-[var(--red)]" : "text-[var(--fg)]")}>{value}</div>
      <div className="text-[10px] text-[var(--fg-muted)]">{label}</div>
    </div>
  );
}

function HealthChip({ kind, label }: { kind: ProjectHealthKind; label: string }) {
  const tone = HEALTH_TONES[kind];
  return (
    <span className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10.5px] font-medium" style={{ color: tone, background: `color-mix(in srgb, ${tone} 14%, transparent)` }}>
      <span className="h-1.5 w-1.5 rounded-full" style={{ background: tone }} />
      {label}
    </span>
  );
}

function EmptyPortfolio() {
  return (
    <div className="flex min-h-[360px] items-center justify-center rounded-md border border-[var(--border)] bg-[var(--bg-secondary)]">
      <div className="max-w-sm px-6 text-center">
        <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full border border-[var(--border)] bg-[var(--bg-tertiary)]">
          <FolderKanban size={18} className="text-[var(--fg-tertiary)]" />
        </div>
        <h3 className="text-[15.5px] font-medium">No project fronts yet</h3>
        <p className="mt-1 text-[12.5px] text-[var(--fg-tertiary)]">Create projects through Pauta’s agent or command menu, then connect tasks, docs, and repositories.</p>
      </div>
    </div>
  );
}

function fallbackNow(insight: ReturnType<typeof deriveProjectInsights>) {
  if (insight.counts.doing || insight.counts.review) {
    return `${insight.counts.doing} doing · ${insight.counts.review} in review`;
  }
  if (insight.next[0]) return `Next: ${insight.next[0].identifier} · ${insight.next[0].title}`;
  if (insight.recentOutcomes[0]) return `Shipped: ${insight.recentOutcomes[0].title}`;
  return "No current work signal yet.";
}

function compareProjects(a: Project, b: Project) {
  const rank: Record<Project["status"], number> = { in_progress: 0, planned: 1, backlog: 2, paused: 3, completed: 4, canceled: 5 };
  return rank[a.status] - rank[b.status] || b.updatedAt - a.updatedAt;
}

function startOfDay(value: number) {
  const date = new Date(value);
  return new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
}

const DAY = 24 * 60 * 60 * 1000;
const HEALTH_TONES: Record<ProjectHealthKind, string> = {
  completed: "var(--green)",
  canceled: "var(--fg-muted)",
  paused: "var(--orange)",
  needs_attention: "var(--red)",
  stale: "var(--yellow)",
  on_track: "var(--green)",
  planned: "var(--blue)",
};
